import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { ScanInput } from '@/modules/wms/components/common/ScanInput';
import { palletAPI } from '@/shared/services/api';
import {
  Search,
  Layers,
  Plus,
  Printer,
  Eye,
  CheckCircle2,
  Clock,
  Package,
  Scale,
  X,
  Check,
  AlertTriangle,
  Loader2 } from
'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';

export const Pallets = () => {const { t } = useTranslation("wms");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, inProgress: 0, completed: 0 });

  // Fetch pallets from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [palletsData, statsData] = await Promise.all([
        palletAPI.getPallets(),
        palletAPI.getStats()]
        );
        setPallets(palletsData || []);
        setStats({
          total: palletsData?.length || 0,
          inProgress: statsData?.building || palletsData?.filter((p) => p.status === 'building' || p.status === 'in_progress').length || 0,
          completed: statsData?.sealed || palletsData?.filter((p) => p.status === 'sealed' || p.status === 'completed').length || 0
        });
      } catch (error) {
        console.error('Failed to fetch pallets:', error);
        toast.error('Failed to load pallets');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleScan = (barcode) => {
    console.log('Scanned package:', barcode);
  };

  return (
    <div className="space-y-6" data-testid="wms-pallets">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("palletBuilder", "Pallet Builder")}</h1>
          <p className="text-muted-foreground mt-1">{t("buildAndManage", "Build and manage pallets for shipment")}

          </p>
        </div>
        <Button
          className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]"
          onClick={() => setShowCreateDialog(true)}>
          
          <Plus className="h-4 w-4" />{t("createNewPallet", "Create New Pallet")}

        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalPallets", "Total Pallets")}</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Layers className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("inProgress", "In Progress")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("completed", "Completed")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Alert for in-progress pallets */}
      {stats.inProgress > 0 &&
      <Card className="card-elevated border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm">{t("youHave", "You have")}
              <span className="font-semibold">{stats.inProgress}{t("pallets", "pallet(s)")}</span>{t("inProgressComplete", "in progress. \n                Complete them before shipping to ensure proper inventory tracking.")}

            </p>
            </div>
          </CardContent>
        </Card>
      }

      {/* Pallets Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("palletsOverview", "Pallets Overview")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search pallets by ID or destination"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("palletId", "Pallet ID")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("packages", "Packages")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("weightKg", "Weight (kg)")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("destination", "Destination")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("created", "Created")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingPallets", "Loading pallets...")}</p>
                    </td>
                  </tr> :
                pallets.length === 0 ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <Layers className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">{t("noPalletsFound", "No pallets found")}</p>
                    </td>
                  </tr> :
                pallets.map((pallet) => {
                  const palletId = pallet.id || pallet.pallet_number;
                  return (
                    <tr key={palletId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{palletId}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{pallet.packages || pallet.carton_count || 0}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <Scale className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{pallet.weight || pallet.total_weight || 0}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">{pallet.destination || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-muted-foreground">
                          {pallet.createdAt || pallet.created_at ?
                          new Date(pallet.createdAt || pallet.created_at).toLocaleDateString() :
                          '-'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <StatusIndicator status={pallet.status} />
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" className="gap-1">
                            <Eye className="h-3.5 w-3.5" />{t("view", "View")}

                          </Button>
                          <Button size="sm" variant="outline" className="gap-1">
                            <Printer className="h-3.5 w-3.5" />{t("print", "Print")}

                          </Button>
                        </div>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Pallet Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />{t("createNewPallet", "Create New Pallet")}

            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Pallet Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <Scale className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t("totalWeight", "Total Weight")}</p>
                <p className="text-xl font-bold">{t("0Kg", "0 kg")}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <Package className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t("totalPackages", "Total Packages")}</p>
                <p className="text-xl font-bold">0</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <Layers className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t("destination", "Destination")}</p>
                <p className="text-sm font-medium">{t("notSet", "Not Set")}</p>
              </div>
            </div>

            {/* Scan Packages */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t("scanPackages", "Scan Packages")}</label>
              <ScanInput
                placeholder="Scan or enter package barcode"
                onScan={handleScan} />
              
            </div>

            {/* Packages List */}
            <div className="border rounded-lg">
              <div className="p-4 text-center text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t("noPackagesAdded", "No packages added yet")}</p>
                <p className="text-xs">{t("scanPackagesTo", "Scan packages to add them to this pallet")}</p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("close", "Close")}

            </Button>
            <Button variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />{t("addPackages", "Add Packages")}

            </Button>
            <Button>{t("finalizePallet", "Finalize Pallet")}

            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};